import React, { MutableRefObject } from 'react';
import { Link } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import numeral from 'numeral';
import * as AppState from '../../../AppState';
import { Property } from '../../../interfaces/interfaces';
import { isCapCenterOffice, isCapCenterListOffice, toTitleCase } from '../../../utilities/util';
import { buildPropertyDetailsUrlWithPrevUrl } from '../../../utilities/util';

import './ListItem.scss';
import ImageCarousel from '../../Shared/ImageCarousel/ImageCarousel';
import ListingStatus from '../../Shared/ListingStatus/ListingStatus';

interface ListItemProps {
	property: Property;
	address: string;
}

function titleCheck(str: string | undefined) {
	return str ? toTitleCase(str) : '';
}

function upperCheck(str: string | undefined) {
	return str ? str.toUpperCase() : '';
}

const ListItem = ({ property, address }: ListItemProps) => {
	const setGsHoveredElement = useSetRecoilState(AppState.gsHoveredElement);
	const setGsClickedMarker = useSetRecoilState(AppState.gsClickedMarker);
	const listItemRef = React.useRef() as MutableRefObject<HTMLDivElement>;

  const openHouses = property.OpenHouse ? property.OpenHouse
    .filter(openHouse=>new Date(openHouse.OpenHouseEndTime)>new Date())
    .sort((a, b)=> new Date(a.OpenHouseStartTime).getTime()-new Date(b.OpenHouseStartTime).getTime())
    : [];

	const addressText =
    (property.StreetNumber ? `${property.StreetNumber} ` : '')
    + (property.StreetDirPrefix ? `${property.StreetDirPrefix}. ` : '')
    + (property.StreetName ? `${titleCheck(property.StreetName)} ` : '')
    + (property.StreetSuffix ? ` ${property.StreetSuffix}` : '')
    + (property.UnitNumber ? `${property.UnitNumber}` : '')
    + ', '
    + (property.City ? property.City + ', ' : property.PostalCity ? property.PostalCity + ', ' : '')
    + (property.StateOrProvince ? `${property.StateOrProvince} ` : '')
    + (property.PostalCode ? `${property.PostalCode} ` : '');

	return (
		<Link to={buildPropertyDetailsUrlWithPrevUrl(property)} target="_blank" title={addressText} tabIndex={0}>
			<div
				id={property.id}
				className={`${property.id} properties-list__list-item h-100`}
				onMouseEnter={() => setGsHoveredElement(prevState => [listItemRef?.current?.id, prevState?.[1]])}
				onMouseLeave={() => setGsHoveredElement(prevState => ['', prevState?.[0]])}
				onClick={() => setGsClickedMarker('')} // removes highlight from other property cards
				ref={listItemRef}
			>
				<div className="list-item__home-image-wrapper">
					<ImageCarousel
						className="image-carousel--property-list-item"
						photos={property.Photos ? property.Photos : []}
						addressText={addressText}
					/>
				</div>
				<div className="list-item__important-details">
					<div className="row g-0 px-3">
						<div className="d-flex align-items-center pt-1">
							<h3 className="text-secondary fw-bold fs-3 mb-1">
								{property.ClosePrice
									? '$' + numeral(property?.ClosePrice?.toString()).format()
									: '$' + numeral(property?.ListPrice?.toString()).format()}
							</h3>
							<ListingStatus
                				isCapcenterPurchase={isCapCenterOffice(property?.BuyerOfficeMlsId, property?.OriginatingSystemName)}
								isCapcenterListing={isCapCenterListOffice(property?.ListOfficeName, property?.ListOfficeMlsId, property?.OriginatingSystemName)}
								onMarket={property?.OnMarketDate || property.DerivedOnMarketTimestamp}
								listingStatus={property?.StandardStatus}
								listItem
								closeDate={property?.CloseDate}
							/>
						</div>
						<p className="fs-5 fw-bold leading--none mb-1">
							{property.StreetNumber && `${property?.StreetNumber} `}
							{property.StreetDirPrefix && `${property.StreetDirPrefix} `}
							{property.StreetName && `${titleCheck(property?.StreetName)}`}
							{property.StreetSuffix && ` ${titleCheck(property.StreetSuffix)}`}
							{property.StreetDirSuffix && ` ${property.StreetDirSuffix}`}
							{property.UnitNumber && ` Unit ${property.UnitNumber}`}
						</p>
						<small className="cc-font-weight--normal leading--none">
							{property.City ? `${titleCheck(property?.City)}, ` : property.CountyOrParish && `${titleCheck(property?.CountyOrParish)}, `}
							{property?.StateOrProvince && `${upperCheck(property?.StateOrProvince)} `}
							{property?.PostalCode && `${titleCheck(property?.PostalCode)}`}
						</small>
					</div>
					<div className='list-item__mls-details cc-font-weight--normal leading--none py-2 px-3 g-0'>
						<div className="row pb-1">
							<small className=" col-12 text-wrap" >
								<span className="text-nowrap">
									{
										`${property?.ListAgentFullName ? `${property.ListAgentFullName}, ` : ''}`
									}
								</span><wbr/>
								<span className="text-nowrap">
									{
										`${property?.ListOfficeName ?? ''}`
									}
								</span>
							</small>
						</div>
						<div className="row">
							<small className=" col-12" >
								<span>
									{property?.ListAOR ?? ''}
								</span>
							</small>
						</div>
					</div>
					<small className="d-flex justify-content-between w-100 px-3">
						<div>{property?.BedroomsTotal || '--'} Bed</div>
						<div className="list-item__vertical-rule--small" />
						<div>{property?.RawMlsBathroomsTotal || '--'} Bath</div>
						<div className="list-item__vertical-rule--small" />
						<div>{property?.LivingArea ? numeral(property?.LivingArea).format() : '--' } Sq Ft</div>
						<div className="list-item__vertical-rule--small" />
						<div>{property?.LotSizeAcres ?  numeral(property?.LotSizeAcres).format('0,0.00') : '--'} Acres</div>
					</small>
          {openHouses.length ?
          <small className='pb-1 px-3'>
            Open House {
            new Date(openHouses[0].OpenHouseStartTime)
              .toLocaleString(new Intl.Locale('en-US'), {month: "numeric", day: "numeric", year:"numeric", hour: "numeric", minute:"numeric"})
            }
          </small> : <></>}
				</div>
			</div>
		</Link>
	);
};

export default ListItem;
